import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = '';

const MODELS = [
  { value: 'nova-canvas', label: 'Nova Canvas', desc: 'Amazon 최신 이미지 생성 모델' },
  { value: 'titan-image', label: 'Titan Image G1 v2', desc: 'Amazon Titan 이미지 생성' },
];

const SIZES = [
  { value: '1024x1024', label: '1024×1024', desc: '정사각형' },
  { value: '1792x1024', label: '1792×1024', desc: '가로형 (16:9)' },
  { value: '1024x1792', label: '1024×1792', desc: '세로형 (9:16)' },
];

const STYLES = [
  { value: 'realistic', label: '사실적', desc: '실사풍 이미지' },
  { value: 'illustration', label: '일러스트', desc: '디지털 일러스트레이션' },
  { value: 'watercolor', label: '수채화', desc: '수채화 스타일' },
  { value: 'oil-painting', label: '유화', desc: '유화 스타일' },
  { value: '3d-render', label: '3D 렌더링', desc: '3D CG 스타일' },
  { value: 'pixel-art', label: '픽셀아트', desc: '레트로 픽셀 스타일' },
  { value: 'anime', label: '애니메이션', desc: '일본 애니메이션 스타일' },
  { value: 'sketch', label: '스케치', desc: '연필 드로잉 스타일' },
];

function ImageAgent() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [model, setModel] = useState('nova-canvas');
  const [size, setSize] = useState('1024x1024');
  const [style, setStyle] = useState('realistic');
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [refineMode, setRefineMode] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const resultRef = useRef(null);


  const handleGenerate = async () => {
    if (!prompt.trim()) return alert('이미지 설명을 입력해주세요.');
    setLoading(true);
    setError('');
    setImages([]);
    try {
      const res = await fetch(`${API}/image/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          negative_prompt: negativePrompt,
          model,
          size,
          style,
          count,
        }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setImages(data.images || []);
        setEnhancedPrompt(data.enhanced_prompt || '');
        setHistory(prev => [
          { prompt, style, size, model, images: data.images || [], enhanced: data.enhanced_prompt || '', timestamp: new Date().toLocaleString() },
          ...prev,
        ].slice(0, 20));
        if (resultRef.current) {
          resultRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (e) {
      setError('이미지 생성에 실패했습니다. 서버 연결을 확인해주세요.');
    }
    setLoading(false);
  };

  const handleRefine = async () => {
    if (!selectedImage || !refinePrompt.trim()) return alert('수정할 이미지와 수정 사항을 입력해주세요.');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/image/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: selectedImage,
          prompt: refinePrompt,
          model,
          size,
          style,
        }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setImages(data.images || []);
        setRefineMode(false);
        setRefinePrompt('');
      }
    } catch (e) {
      setError('이미지 수정에 실패했습니다.');
    }
    setLoading(false);
  };

  const handleDownload = async (imageUrl, index) => {
    try {
      const res = await fetch(`${API}/image/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: imageUrl }),
      });
      const prepData = await res.json(); if (prepData.file_id) { window.open(`${API}/get-download/${prepData.file_id}`, '_self'); return; }
    } catch (e) {
      alert('다운로드에 실패했습니다.');
    }
  };

  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 32px', background: '#fff', borderBottom: '1px solid #e0e0e0',
  };
  const bodyStyle = {
    display: 'flex', gap: 32, padding: 32, maxWidth: 1400, margin: '0 auto',
    fontFamily: "'Pretendard', 'Noto Sans KR', Arial, sans-serif",
  };
  const panelStyle = {
    background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    padding: 28, flex: 1,
  };
  const labelStyle = { fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'block', color: '#333' };
  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #d0d0d0',
    fontSize: 15, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
  };
  const btnPrimary = {
    padding: '14px 0', width: '100%', border: 'none', borderRadius: 12,
    background: 'linear-gradient(135deg, #e040fb 0%, #7c4dff 100%)',
    color: '#fff', fontWeight: 700, fontSize: 17, cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(124,77,255,0.3)', transition: 'transform 0.1s',
  };
  const chipStyle = (active) => ({
    display: 'inline-block', padding: '8px 16px', borderRadius: 20,
    border: active ? '2px solid #7c4dff' : '1px solid #d0d0d0',
    background: active ? '#f3e8fd' : '#fafafa',
    color: active ? '#7c4dff' : '#555', fontWeight: active ? 700 : 400,
    fontSize: 13, cursor: 'pointer', margin: '0 6px 8px 0', transition: 'all 0.15s',
  });
  const imageCardStyle = {
    borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    background: '#fff', transition: 'transform 0.2s',
  };

  return (
    <div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate('/')} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#7c4dff',
          }}>←</button>
          <div>
            <div style={{ fontWeight: 800, fontSize: 20, color: '#333' }}>
              <span style={{ color: '#7c4dff' }}>🎨</span> 이미지 생성 에이전트
            </div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
              Nova Canvas / Titan Image / 다양한 스타일
            </div>
          </div>
        </div>
      </div>

      <div style={bodyStyle}>
        {/* Left Panel - Controls */}
        <div style={{ ...panelStyle, maxWidth: 420, flex: '0 0 420px' }}>
          <label style={labelStyle}>프롬프트 (이미지 설명)</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="생성할 이미지를 자세히 설명해주세요.&#10;예) 석양이 지는 바닷가에서 서핑하는 골든 리트리버, 사실적인 사진 스타일"
            rows={5}
            style={{ ...inputStyle, marginBottom: 16 }}
          />

          <label style={labelStyle}>네거티브 프롬프트 (제외할 요소)</label>
          <textarea
            value={negativePrompt}
            onChange={e => setNegativePrompt(e.target.value)}
            placeholder="이미지에서 제외할 요소 (선택사항)&#10;예) 흐릿한, 저화질, 워터마크"
            rows={2}
            style={{ ...inputStyle, marginBottom: 20 }}
          />

          <label style={labelStyle}>모델</label>
          <div style={{ marginBottom: 16 }}>
            {MODELS.map(m => (
              <span key={m.value} onClick={() => setModel(m.value)} style={chipStyle(model === m.value)}
                title={m.desc}>{m.label}</span>
            ))}
          </div>

          <label style={labelStyle}>스타일</label>
          <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}>
            {STYLES.map(s => (
              <span key={s.value} onClick={() => setStyle(s.value)} style={chipStyle(style === s.value)}
                title={s.desc}>{s.label}</span>
            ))}
          </div>

          <label style={labelStyle}>이미지 크기</label>
          <div style={{ marginBottom: 16 }}>
            {SIZES.map(s => (
              <span key={s.value} onClick={() => setSize(s.value)} style={chipStyle(size === s.value)}
                title={s.desc}>{s.label}</span>
            ))}
          </div>

          <label style={labelStyle}>생성 수량: {count}장</label>
          <input
            type="range" min={1} max={4} value={count}
            onChange={e => setCount(Number(e.target.value))}
            style={{ width: '100%', marginBottom: 24 }}
          />

          <button onClick={handleGenerate} disabled={loading} style={{
            ...btnPrimary, opacity: loading ? 0.7 : 1,
          }}>
            {loading ? '✨ 이미지 생성 중...' : '🎨 이미지 생성'}
          </button>
        </div>

        {/* Right Panel - Results */}
        <div style={{ ...panelStyle, flex: 1 }} ref={resultRef}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 16, color: '#333' }}>
            생성 결과
          </div>

          {error && (
            <div style={{
              padding: 16, borderRadius: 12, background: '#ffebee', color: '#c62828',
              fontSize: 14, marginBottom: 16,
            }}>{error}</div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#888' }}>
              <div style={{
                width: 48, height: 48, border: '4px solid #e0e0e0', borderTop: '4px solid #7c4dff',
                borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px',
              }} />
              <div style={{ fontSize: 15 }}>AI가 이미지를 생성하고 있습니다...</div>
              <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>약 10~30초 소요됩니다</div>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {!loading && images.length === 0 && !error && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🖼️</div>
              <div style={{ fontSize: 15 }}>프롬프트를 입력하고 이미지를 생성해보세요</div>
            </div>
          )}

          {enhancedPrompt && images.length > 0 && (
            <div style={{
              padding: '12px 16px', borderRadius: 10, background: '#f3e8fd',
              border: '1px solid #d1c4e9', marginBottom: 16, fontSize: 13, color: '#555',
            }}>
              <span style={{ fontWeight: 700, color: '#7c4dff' }}>AI 변환 프롬프트: </span>
              {enhancedPrompt}
            </div>
          )}

          {images.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: images.length === 1 ? '1fr' : 'repeat(2, 1fr)',
              gap: 20,
            }}>
              {images.map((img, i) => (
                <div key={i} style={imageCardStyle}>
                  <img
                    src={img.url.startsWith('http') ? img.url : `${API}${img.url}`}
                    alt={`Generated ${i + 1}`}
                    style={{ width: '100%', display: 'block', cursor: 'pointer' }}
                    onClick={() => { setSelectedImage(img.url); setRefineMode(true); }}
                  />
                  <div style={{ padding: '12px 16px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={() => handleDownload(img.url, i)} style={{
                      padding: '8px 16px', borderRadius: 8, border: '1px solid #d0d0d0',
                      background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}>💾 다운로드</button>
                    <button onClick={() => { setSelectedImage(img.url); setRefineMode(true); }} style={{
                      padding: '8px 16px', borderRadius: 8, border: 'none',
                      background: '#7c4dff', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}>✏️ 수정</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Refine Panel */}
          {refineMode && selectedImage && (
            <div style={{
              marginTop: 24, padding: 20, borderRadius: 12, background: '#f3e8fd',
              border: '1px solid #d1c4e9',
            }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, color: '#7c4dff' }}>
                이미지 수정
              </div>
              <textarea
                value={refinePrompt}
                onChange={e => setRefinePrompt(e.target.value)}
                placeholder="수정할 내용을 입력해주세요.&#10;예) 배경을 밤하늘로 변경, 색감을 더 따뜻하게"
                rows={3}
                style={{ ...inputStyle, marginBottom: 12 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleRefine} disabled={loading} style={{
                  padding: '10px 24px', borderRadius: 8, border: 'none',
                  background: '#7c4dff', color: '#fff', fontWeight: 600, cursor: 'pointer',
                }}>수정 적용</button>
                <button onClick={() => { setRefineMode(false); setRefinePrompt(''); }} style={{
                  padding: '10px 24px', borderRadius: 8, border: '1px solid #d0d0d0',
                  background: '#fff', cursor: 'pointer', fontWeight: 600,
                }}>취소</button>
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, color: '#333' }}>
                생성 히스토리
              </div>
              {history.map((h, i) => (
                <div key={i} style={{
                  padding: '12px 16px', borderRadius: 10, background: '#fafafa',
                  border: '1px solid #eee', marginBottom: 8, cursor: 'pointer',
                }} onClick={() => { setPrompt(h.prompt); setStyle(h.style); setSize(h.size); setModel(h.model); }}>
                  <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{h.timestamp}</span>
                    {' · '}{h.style} · {h.size} · {h.images.length}장
                  </div>
                  <div style={{ fontSize: 14, color: '#333' }}>
                    {h.prompt.length > 80 ? h.prompt.slice(0, 80) + '...' : h.prompt}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImageAgent;
